import { MIN_EMPLOYEES_FOR_QUOTES } from '../../constants/quotes';
import type {
  IActivationCensusValidationErrorsResult,
  IActivationCensusValidationStatusResult,
  IActivationCensusValidationSummary,
  ISendActivationCensusToValidateResult
} from '../../interfaces/quotes';
import QuotesService from '../../services/api/quotes/quotes';
import { ActivationCensusValidationStatus, type CensusVariant } from '../../types/quotes';

export const CENSUS_STATUS_POLL_INTERVAL_MS = 5000;
export const CENSUS_STATUS_POLL_TIMEOUT_MS = 60_000;
export const CENSUS_STATUS_MAX_POLLS = CENSUS_STATUS_POLL_TIMEOUT_MS / CENSUS_STATUS_POLL_INTERVAL_MS + 1;

export type ActivationCensusStep =
  | 'upload'
  | 'validating'
  | 'processing'
  | 'success'
  | 'error-format'
  | 'error-data-many'
  | 'price-mismatch'
  | 'error-minimum'
  | 'error-service';

export interface IActivationPriceMismatch {
  originalPrice: number;
  newPrice: number;
  reason: string;
}

export interface IActivationCensusValidationArgs {
  brokerId: string | null;
  prospectId: number;
  quoteId: number;
  file: File;
  variant?: CensusVariant;
}

export async function sendActivationCensusToValidate({
  brokerId,
  prospectId,
  quoteId,
  file,
  variant
}: IActivationCensusValidationArgs): Promise<ISendActivationCensusToValidateResult> {
  if (!brokerId) throw new Error('Missing brokerId');
  return QuotesService.sendActivationCensusToValidate(brokerId, prospectId, quoteId, file, variant);
}

export async function getActivationCensusValidationStatus(
  brokerId: string | null,
  requestId: string
): Promise<IActivationCensusValidationStatusResult> {
  if (!brokerId) throw new Error('Missing brokerId');
  return QuotesService.getActivationCensusValidationStatus(brokerId, requestId);
}

export async function getActivationCensusValidationErrors(
  brokerId: string | null,
  requestId: string,
  variant?: CensusVariant
): Promise<IActivationCensusValidationErrorsResult> {
  if (!brokerId) throw new Error('Missing brokerId');
  return QuotesService.getActivationCensusValidationErrors(brokerId, requestId, variant);
}

export const isCensusValidationCompleted = (status: IActivationCensusValidationStatusResult): boolean =>
  status.status === ActivationCensusValidationStatus.Completed;

export const isCensusParseError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 400;

export const isCensusRequestNotFound = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 404;

export function resolveCompletedStep(
  summary: IActivationCensusValidationSummary,
  hasError: boolean,
  requiresMinimum = true
): ActivationCensusStep {
  if (summary.withErrors > 0) return 'error-data-many';
  if (hasError) return 'error-service';
  const minimum = requiresMinimum ? MIN_EMPLOYEES_FOR_QUOTES : 1;
  if (summary.total < minimum) return 'error-minimum';
  return 'success';
}

export interface IStoredCensusEntry {
  requestId: string;
  fileName: string | null;
}

const STORED_CENSUS_ENTRY_PREFIX = 'activation_census_request_';

const storedCensusEntryKey = (quoteId: number) => `${STORED_CENSUS_ENTRY_PREFIX}${quoteId}`;

export function getStoredCensusEntry(quoteId: number): IStoredCensusEntry | null {
  try {
    const raw = sessionStorage.getItem(storedCensusEntryKey(quoteId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.requestId !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setStoredCensusEntry(quoteId: number, entry: IStoredCensusEntry): void {
  try {
    sessionStorage.setItem(storedCensusEntryKey(quoteId), JSON.stringify(entry));
  } catch {
    /* noop */
  }
}

export function clearStoredCensusEntry(quoteId: number): void {
  try {
    sessionStorage.removeItem(storedCensusEntryKey(quoteId));
  } catch {
    /* noop */
  }
}

export function clearAllStoredCensusEntries(): void {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(STORED_CENSUS_ENTRY_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* noop */
  }
}

export type { IActivationCensusErrorsFile, IActivationCensusValidationSummary } from '../../interfaces/quotes';
